import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Loader } from "@/components/Loader";
import { useEffect, useState } from "react";
import supabase from "@/lib/supabase";

interface UnProtectedRouteProps {
  children: React.ReactNode;
}

export function UnProtectedRoute({ children }: UnProtectedRouteProps) {
  const { user, session, loading, initialized, signingOut, clearingAuth } =
    useAuthStore();
  const location = useLocation();
  const [validatingSession, setValidatingSession] = useState(false);
  const [sessionValid, setSessionValid] = useState<boolean | null>(null);

  // Validate session when component mounts if user appears authenticated
  useEffect(() => {
    const validateSession = async () => {
      if (user && session && !signingOut && !clearingAuth) {
        setValidatingSession(true);
        try {
          // Verify the session is actually valid
          const {
            data: { session: currentSession },
          } = await supabase.auth.getSession();

          if (currentSession?.access_token) {
            // Session is valid
            setSessionValid(true);
          } else {
            // Session is invalid, clear it
            console.log("Session validation failed, clearing auth state");
            useAuthStore.getState().clearStore();
            setSessionValid(false);
          }
        } catch (error) {
          console.error("Error validating session:", error);
          useAuthStore.getState().clearStore();
          setSessionValid(false);
        } finally {
          setValidatingSession(false);
        }
      } else if (!user || !session) {
        setSessionValid(false);
      }
    };

    validateSession();
  }, [user, session, signingOut, clearingAuth]);

  // Show loading while auth state is being initialized or validating session
  if (!initialized || loading || validatingSession) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <Loader fullScreen size="lg" />
      </div>
    );
  }

  // Don't redirect if currently signing out or clearing auth (prevents cyclic navigation)
  if (signingOut || clearingAuth) {
    return <>{children}</>;
  }

  // Redirect to admin if authenticated and session is valid
  if (user && session && sessionValid === true) {
    console.log("Valid session found, redirecting to admin");
    return <Navigate to="/admin" state={{ from: location }} replace />;
  }

  // User is not authenticated, render the unprotected content
  return <>{children}</>;
}

export default UnProtectedRoute;
